import greek700 from '@fontsource/fira-sans/files/fira-sans-greek-700-normal.woff2?url';
import greek900 from '@fontsource/fira-sans/files/fira-sans-greek-900-italic.woff2?url';
import latin700 from '@fontsource/fira-sans/files/fira-sans-latin-700-normal.woff2?url';
import latin900 from '@fontsource/fira-sans/files/fira-sans-latin-900-italic.woff2?url';
import latinExt700 from '@fontsource/fira-sans/files/fira-sans-latin-ext-700-normal.woff2?url';
import latinExt900 from '@fontsource/fira-sans/files/fira-sans-latin-ext-900-italic.woff2?url';
import { needsGreek, needsLatinExt } from './text';

/**
 * The font files the PNG export embeds. An SVG drawn as an image cannot see the page's fonts or
 * load any file, so the card's two faces (Fira Sans 900 italic for numbers and names, 700 for the
 * subline) travel inside the SVG as data URIs. The files are the same hashed assets the app's own
 * `@fontsource` CSS already ships (the service worker precaches them), so this costs no extra bytes
 * in the bundle. Subsets are picked from the text, so a Latin card embeds about 50 KB.
 */

export type Subset = 'latin' | 'latin-ext' | 'greek';

interface Face {
  weight: 700 | 900;
  style: 'normal' | 'italic';
  subset: Subset;
  url: string;
}

const FACES: Face[] = [
  { weight: 900, style: 'italic', subset: 'latin', url: latin900 },
  { weight: 900, style: 'italic', subset: 'latin-ext', url: latinExt900 },
  { weight: 900, style: 'italic', subset: 'greek', url: greek900 },
  { weight: 700, style: 'normal', subset: 'latin', url: latin700 },
  { weight: 700, style: 'normal', subset: 'latin-ext', url: latinExt700 },
  { weight: 700, style: 'normal', subset: 'greek', url: greek700 },
];

/** The unicode-range of each subset, as @fontsource declares them. */
export const UNICODE_RANGE: Record<Subset, string> = {
  latin:
    'U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD',
  'latin-ext':
    'U+0100-02BA,U+02BD-02C5,U+02C7-02CC,U+02CE-02D7,U+02DD-02FF,U+0304,U+0308,U+0329,U+1D00-1DBF,U+1E00-1E9F,U+1EF2-1EFF,U+2020,U+20A0-20AB,U+20AD-20C0,U+2113,U+2C60-2C7F,U+A720-A7FF',
  greek: 'U+0370-0377,U+037A-037F,U+0384-038A,U+038C,U+038E-03A1,U+03A3-03FF',
};

/** Which subsets a piece of text needs. Latin is always in. */
export function subsetsFor(text: string): Subset[] {
  const out: Subset[] = ['latin'];
  if (needsLatinExt(text)) out.push('latin-ext');
  if (needsGreek(text)) out.push('greek');
  return out;
}

/** The faces for those subsets. */
export function facesFor(subsets: Subset[]): Face[] {
  return FACES.filter((f) => subsets.includes(f.subset));
}

/** `@font-face` rules with the font data inlined; `toDataUri` turns a file URL into a data URI. */
export async function fontFaceCss(subsets: Subset[], toDataUri: (url: string) => Promise<string>): Promise<string> {
  const rules = await Promise.all(
    facesFor(subsets).map(async (f) => {
      const data = await toDataUri(f.url);
      return `@font-face{font-family:'Fira Sans';font-style:${f.style};font-weight:${f.weight};src:url(${data}) format('woff2');unicode-range:${UNICODE_RANGE[f.subset]};}`;
    }),
  );
  return rules.join('');
}
